<script>
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';

export default {
  name: 'PromotionCard',
  components: {
    AoikumoDisabledTooltip,
  },
  props: {
    promotion: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isHovered: false,
      isActive: false,
      loading: false,
    };
  },
  watch: {
    'promotion.is_active': {
      immediate: true,
      handler(val) {
        this.isActive = val;
      },
    },
  },
  computed: {
    assignments() {
      return this.promotion.assignments || [];
    },
    totalServices() {
      return this.assignments.reduce(
        (sum, a) => sum + (a.services?.length || 0),
        0
      );
    },
  },
  methods: {
    async handleToggle() {
      if (this.loading || this.readOnly) return;

      const previous = this.isActive;
      const next = !previous;

      this.isActive = next;
      this.loading = true;

      try {
        this.$emit('toggle', {
          id: this.promotion.id,
          value: next,
        });
      } catch (err) {
        this.isActive = previous;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="card relative rounded-lg border shadow-md hover:shadow-lg transition-all bg-white dark:bg-slate-800 dark:text-white overflow-hidden group flex flex-col h-full border-slate-200 dark:border-slate-700"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Header Section (Status + Price) -->
    <div class="relative p-5 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between">
      <!-- Price -->
      <span class="px-3 py-1 rounded-md text-sm font-semibold bg-slate-700 dark:bg-slate-600 text-white dark:text-slate-100">
        {{ promotion.price }}
      </span>

      <!-- Status -->
      <span
        class="px-2 py-1 rounded-full text-xs font-medium"
        :class="
          isActive
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
        "
      >
        {{ isActive ? 'Active' : 'Inactive' }}
      </span>
    </div>

    <!-- Hover Actions -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <AoikumoDisabledTooltip
        v-if="isHovered"
        :disabled="readOnly"
        class="absolute top-3 right-3 flex gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg p-1 z-20"
        role="toolbar"
        aria-label="Promotion actions"
      >
        <woot-button
          :icon="isActive ? 'toggle-left' : 'toggle-right'"
          variant="clear"
          :color-scheme="isActive ? 'primary' : 'secondary'"
          :disabled="loading || readOnly"
          @click="handleToggle"
        />
        <button :disabled="readOnly" @click.stop="$emit('edit')" class="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button :disabled="readOnly" @click.stop="$emit('delete')" class="p-2 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </AoikumoDisabledTooltip>
    </transition>

    <!-- Content Section -->
    <div class="p-5 flex-1 flex flex-col">
      <!-- Title & Description -->
      <div class="mb-3">
        <h2 class="font-bold text-lg text-slate-800 dark:text-white line-clamp-1 mb-1">
          {{ promotion.promotion_name }}
        </h2>
        <p class="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
          {{ promotion.description || 'No description provided' }}
        </p>
      </div>

      <div class="flex gap-3">
        <div class="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-800">
          <span class="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Branches
          </span>
          <span class="text-sm font-semibold text-slate-900 dark:text-white">
            {{ promotion.branch_count || 0 }}
          </span>
        </div>

        <div class="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-slate-800">
          <span class="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Services
          </span>
          <span class="text-sm font-semibold text-slate-900 dark:text-white">
            {{ promotion.service_count || 0 }}
          </span>
        </div>
      </div>

      <!-- Branch & Services (Scrollable) -->
      <!-- <div
        class="flex-1 overflow-y-auto pr-1 space-y-2 text-xs
              scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600"
      >
        <div
          v-for="(assignment, index) in assignments"
          :key="index"
          class="border rounded-md p-2 border-slate-200 dark:border-slate-600"
        >
          <p class="font-semibold text-slate-700 dark:text-slate-200 mb-1">
            {{ assignment.branch.name || 'Unknown Branch' }}
          </p>

          <ul class="pl-4 list-disc text-slate-600 dark:text-slate-400">
            <li
              v-for="service in assignment.services"
              :key="service.id"
            >
              {{ service.name }}
            </li>
            <li v-if="!assignment.services.length" class="italic text-slate-500 dark:text-slate-500">
              No services linked
            </li>
          </ul>
        </div>

        <p
          v-if="!assignments.length"
          class="text-center italic text-slate-500"
        >
          No branches linked
        </p>
      </div> -->

      <!-- Footer -->
      <div
        class="flex items-center justify-between pt-2 mt-3 border-t border-slate-200 dark:border-slate-700"
      >
        <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">
          Expired date: {{ promotion.expired_date || '-' }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-1 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.card {
  min-height: 280px;
}
</style>